import React from 'react';
import { useTranslation } from 'react-i18next';
import { ExadisReturnListLine } from '@1po/1po-bff-fe-spec/generated/exadis_return_list/model/ExadisReturnListLine';
import { Table } from 'antd-v5';
import { CenterFlex, Empty, Flex, Text, WithTooltip } from 'UI';
import { isLoading, SEARCH_STATUS, textFormatter } from 'utils';
import { isValidDate } from 'utils/format/formattingService';

interface ExadisReturnListTableProps {
  items: ExadisReturnListLine[];
  fetchStatus: SEARCH_STATUS;
}

const ExadisReturnListTable = ({ items, fetchStatus }: ExadisReturnListTableProps): JSX.Element => {
  const { t } = useTranslation();

  const getColumns = () => [
    {
      title: t('exadis_return_list.table.order_number', 'Order N°'),
      dataIndex: 'salesOrderNumber',
      key: 'salesOrderNumber',
      render: (salesOrderNumber: string) => <CenterFlex>{salesOrderNumber ?? ''}</CenterFlex>,
    },
    {
      title: t('exadis_return_list.table.supplier', 'Supplier'),
      dataIndex: 'supplierName',
      key: 'supplierName',
      render: (supplierName?: string) => (
        <Flex justify={'center'} align={'center'}>
          <WithTooltip title={supplierName}>
            <Text type={'text_dim'}>{supplierName ?? ''}</Text>
          </WithTooltip>
        </Flex>
      ),
    },
    {
      title: t('exadis_return_list.table.reference', 'Reference'),
      dataIndex: 'reference',
      key: 'reference',
      render: (reference: string) => <CenterFlex>{reference ?? ''}</CenterFlex>,
    },
    {
      title: t('exadis_return_list.table.return_qty', 'Return Qty'),
      dataIndex: 'requestedQuantity',
      key: 'requestedQuantity',
      render: (requestedQuantity: number) => <CenterFlex>{requestedQuantity ?? ''}</CenterFlex>,
    },
    {
      title: t('exadis_return_list.table.delivery_number', 'Delivery Number'),
      dataIndex: 'deliveryNumber',
      key: 'deliveryNumber',
      render: (deliveryNumber?: string) => <CenterFlex>{deliveryNumber ?? ''}</CenterFlex>,
    },
    {
      title: t('exadis_return_list.table.delivery_date', 'Delivery Date'),
      dataIndex: 'deliveryDate',
      key: 'deliveryDate',
      render: (deliveryDate?: string) => {
        if (!deliveryDate || !isValidDate(deliveryDate)) {
          return null;
        }
        return <CenterFlex>{textFormatter.formatDateNumeric(new Date(deliveryDate))}</CenterFlex>;
      },
    },
    {
      title: t('exadis_return_list.table.invoice_number', 'Invoice N°'),
      dataIndex: 'invoiceNumber',
      key: 'invoiceNumber',
      render: (invoiceNumber: string) => <CenterFlex>{invoiceNumber ?? ''}</CenterFlex>,
    },
    {
      title: t('exadis_return_list.table.invoice_line_number', 'Invoice line N°'),
      dataIndex: 'invoiceLineNumber',
      key: 'invoiceLineNumber',
      render: (invoiceLineNumber?: number) => <CenterFlex>{invoiceLineNumber ?? ''}</CenterFlex>,
    },
    {
      title: t('exadis_return_list.table.invoice_date', 'Invoice date'),
      dataIndex: 'invoiceDate',
      key: 'invoiceDate',
      render: (invoiceDate?: string) => {
        if (!invoiceDate || !isValidDate(invoiceDate)) {
          return null;
        }
        return <CenterFlex>{textFormatter.formatDateNumeric(new Date(invoiceDate))}</CenterFlex>;
      },
    },
  ];

  return (
    <Table
      bordered
      dataSource={items}
      pagination={false}
      columns={getColumns()}
      loading={isLoading(fetchStatus)}
      rowKey={(row) => `${row.salesOrderNumber}-${row.invoiceId}-${row.invoiceLineNumber}-${row.reference}`}
      scroll={{ x: true }}
      locale={{
        emptyText: <Empty description={t('common.table.empty', 'No results found')} />,
      }}
      data-cy={'exadis-return-list-table'}
    />
  );
};

export default ExadisReturnListTable;
